
import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Heart, Eye, Star, Zap, Sparkles } from 'lucide-react';
import { Product } from '@/types/common';
import { useNavigate } from 'react-router-dom';
import { handleProductNavigation } from '@/utils/productUrlUtils';
import { cn } from '@/lib/utils';
import { useOptimizedCart } from '@/contexts/OptimizedCartContext';
import { useToast } from '@/hooks/use-toast';

interface Premium3DProductCardProps {
  product: Product;
  index: number;
  isActive?: boolean;
  className?: string;
}

const Premium3DProductCard = ({ product, index, isActive = false, className }: Premium3DProductCardProps) => {
  const navigate = useNavigate();
  const { addToCart } = useOptimizedCart();
  const { toast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Enhanced 3D Transform values
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), {
    stiffness: 300,
    damping: 30
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), {
    stiffness: 300,
    damping: 30
  });

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    x.set((event.clientX - centerX) / rect.width);
    y.set((event.clientY - centerY) / rect.height);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    x.set(0);
    y.set(0);
  };

  const handleProductClick = () => {
    handleProductNavigation(product, navigate);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product);
    toast({
      title: "✨ Added to Collection",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    toast({
      title: "💖 Added to Wishlist",
      description: `${product.name} has been saved to your wishlist.`,
    });
  };

  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  const discountPercentage = hasDiscount 
    ? Math.round(((product.compare_at_price! - product.price) / product.compare_at_price!) * 100)
    : 0;

  return (
    <motion.div
      ref={cardRef}
      className={cn("group perspective-1000 cursor-pointer h-full", className)}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        scale: isActive ? 1.02 : 1,
      }}
      transition={{ delay: index * 0.1, duration: 0.6, type: "spring", stiffness: 100 }}
      whileHover={{ 
        y: -8,
        scale: 1.02,
        transition: { type: "spring", stiffness: 400, damping: 25 }
      }}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d"
      }}
    >
      <Card 
        className="relative overflow-hidden cursor-pointer border-0 bg-gradient-to-br from-card/95 to-card/90 backdrop-blur-sm shadow-lg hover:shadow-2xl transition-all duration-700 h-full flex flex-col"
        onClick={handleProductClick}
        style={{ transform: "translateZ(50px)" }}
      >
        {/* Premium Badges */}
        <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
          {product.featured && (
            <motion.div
              className="bg-gradient-to-r from-amber-400 to-amber-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: index * 0.1 + 0.3, type: "spring", stiffness: 300 }}
              style={{ transform: "translateZ(80px)" }}
            >
              <Sparkles className="h-3 w-3" />
              FEATURED
            </motion.div>
          )}
          {hasDiscount && (
            <motion.div
              className="bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.1 + 0.4, type: "spring" }}
              style={{ transform: "translateZ(80px)" }}
            >
              -{discountPercentage}%
            </motion.div>
          )}
        </div>

        {/* Quick Actions */}
        <motion.div
          className="absolute top-3 right-3 z-20 flex flex-col gap-2"
          initial={{ opacity: 0, x: 20 }}
          animate={{ 
            opacity: isHovered ? 1 : 0,
            x: isHovered ? 0 : 20
          }}
          transition={{ duration: 0.3 }}
          style={{ transform: "translateZ(80px)" }}
        >
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
            <Button
              size="sm"
              variant="secondary"
              className="w-8 h-8 p-0 bg-background/80 backdrop-blur-md hover:bg-background/90 border-0 shadow-lg rounded-full"
              onClick={handleWishlist}
            >
              <Heart className="h-3 w-3 text-red-500" />
            </Button>
          </motion.div>
        </motion.div>

        {/* Enhanced Product Image */}
        <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-muted/20 to-muted/10">
          {!imageError ? (
            <>
              <motion.img
                src={product.image_url || '/placeholder.svg'}
                alt={product.name}
                className={`w-full h-full object-cover transition-all duration-700 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ transform: "translateZ(30px)" }}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.7, type: "spring", stiffness: 200 }}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
              
              {/* Loading skeleton */}
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted/50 to-muted animate-pulse" />
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 bg-muted-foreground/20 rounded-full flex items-center justify-center">
                  <Eye className="h-6 w-6" />
                </div>
                <p className="text-sm">Image unavailable</p>
              </div>
            </div>
          )}
          
          {/* Enhanced Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          />

          {/* Floating Add to Cart */}
          <motion.div
            className="absolute bottom-4 left-4 right-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: isHovered ? 1 : 0,
              y: isHovered ? 0 : 30
            }}
            transition={{ duration: 0.4, delay: 0.1 }}
            style={{ transform: "translateZ(60px)" }}
          >
            <Button
              className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground border-0 shadow-lg font-semibold rounded-full"
              onClick={handleAddToCart}
              size="sm"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Add to Cart
            </Button>
          </motion.div>

          {/* Premium Shine Effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0"
            animate={{
              x: isHovered ? '300%' : '-100%',
              opacity: isHovered ? [0, 1, 0] : 0
            }}
            transition={{
              duration: 1.2,
              ease: "easeInOut"
            }}
            style={{ transform: "translateZ(5px)" }}
          />
        </div>

        <CardContent className="p-4 space-y-3 flex-1 flex flex-col" style={{ transform: "translateZ(30px)" }}>
          {/* Category */}
          {product.categories?.name && (
            <motion.p 
              className="text-xs text-muted-foreground uppercase tracking-wider font-medium"
              animate={{ y: isHovered ? -2 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {product.categories.name}
            </motion.p>
          )}

          {/* Product Name */}
          <motion.h3 
            className="font-bold text-lg leading-tight line-clamp-2 group-hover:text-primary transition-colors duration-300 flex-1"
            animate={{ y: isHovered ? -2 : 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
          >
            {product.name}
          </motion.h3>

          {/* Rating */}
          <motion.div 
            className="flex items-center gap-2"
            animate={{ y: isHovered ? -2 : 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1 + i * 0.03 }}
                >
                  <Star
                    className={cn(
                      "h-3 w-3 transition-colors",
                      i < 4 ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"
                    )}
                  />
                </motion.div>
              ))}
            </div>
            <span className="text-xs text-muted-foreground font-medium">(4.8)</span>
          </motion.div>

          {/* Price Section */}
          <motion.div 
            className="flex items-baseline gap-2 pt-2"
            animate={{ y: isHovered ? -2 : 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            <span className="text-xl font-bold text-primary">
              ${product.price?.toFixed(2)}
            </span>
            {hasDiscount && (
              <span className="text-sm text-muted-foreground line-through">
                ${product.compare_at_price!.toFixed(2)}
              </span>
            )}
          </motion.div>
        </CardContent>

        {/* Premium Glow Effect */}
        <motion.div
          className="absolute -inset-1 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10"
          animate={{
            opacity: isActive ? 0.2 : 0
          }}
        />
      </Card>
    </motion.div>
  );
};

export default Premium3DProductCard;
